import { useState, useEffect } from 'react'
import { Drawer, VStack, HStack, Box, Text, Avatar, Button, Portal, CloseButton } from '@chakra-ui/react';
import type { ItineraryAssignmentDto, ItineraryCanvasserRowDto } from '../../../types/campaign'
import { getWeekDates } from './DayChipSwitcher'
import { AuthedAvatarImage } from '../../shared'

interface MoveAssignmentSheetProps {
  isOpen: boolean
  onClose: () => void
  assignment: ItineraryAssignmentDto | null
  canvassers: ItineraryCanvasserRowDto[]
  weekStart: string
  isStatic: boolean
  onMove: (targetUserId: string, targetDateStr: string) => void
}

export function MoveAssignmentSheet({ isOpen, onClose, assignment, canvassers, weekStart, isStatic, onMove }: MoveAssignmentSheetProps) {
  const [targetUserId, setTargetUserId] = useState<string | null>(null)
  const [targetDate, setTargetDate] = useState<string>(assignment?.scheduledDate ?? weekStart)

  useEffect(() => {
    if (assignment) { setTargetUserId(assignment.assignedGrantId); setTargetDate(assignment.scheduledDate) }
  }, [assignment])

  const weekDates = getWeekDates(weekStart)

  return (
    <Drawer.Root open={isOpen} placement='bottom' onOpenChange={e => {
      if (!e.open) {
        onClose();
      }
    }}>
      <Portal>

        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)">
            <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
            <Drawer.Header borderBottomWidth="1px">Move {assignment?.turfLabel ?? 'assignment'}</Drawer.Header>
            <Drawer.Body>
              <VStack gap={4} align="stretch" py={2}>
                <Box>
                  <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>Canvasser</Text>
                  <VStack gap={1} align="stretch">
                    {canvassers.map(c => (
                      <HStack
                        key={c.grantId}
                        p={2}
                        minH="48px"
                        borderRadius="md"
                        borderWidth="1px"
                        borderColor={targetUserId === c.grantId ? 'accent' : 'gray.200'}
                        bg={targetUserId === c.grantId ? 'accent.soft' : 'white'}
                        asChild><button onClick={() => setTargetUserId(c.grantId)}>
                          <Avatar.Root size="xs"><Avatar.Fallback name={c.fullName} /><AuthedAvatarImage src={c.portraitUrl} /></Avatar.Root>
                          <Text fontSize="sm">{c.fullName}</Text>
                        </button></HStack>
                    ))}
                  </VStack>
                </Box>

                {!isStatic && (
                  <Box>
                    <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={1}>Day</Text>
                    <HStack gap={2} overflowX="auto" pb={1}>
                      {weekDates.map(({ dateStr, dayLabel, dayNum }) => (
                        <VStack
                          key={dateStr}
                          minW="52px"
                          h="52px"
                          justify="center"
                          borderRadius="lg"
                          borderWidth="1px"
                          flexShrink={0}
                          borderColor={targetDate === dateStr ? 'accent' : 'gray.200'}
                          bg={targetDate === dateStr ? 'accent' : 'white'}
                          color={targetDate === dateStr ? 'white' : 'gray.700'}
                          gap={0}
                          asChild><button onClick={() => setTargetDate(dateStr)}>
                            <Text fontSize="2xs" fontWeight="bold">{dayLabel}</Text>
                            <Text fontSize="xs">{dayNum}</Text>
                          </button></VStack>
                      ))}
                    </HStack>
                  </Box>
                )}
              </VStack>
            </Drawer.Body>
            <Drawer.Footer borderTopWidth="1px">
              <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
              <Button
                colorPalette="brand"
                disabled={!targetUserId}
                onClick={() => { if (targetUserId) { onMove(targetUserId, isStatic ? assignment!.scheduledDate : targetDate); onClose() } }}
              >
                Move
              </Button>
            </Drawer.Footer>
          </Drawer.Content>
        </Drawer.Positioner>

      </Portal>
    </Drawer.Root>
  );
}
